import React, { useMemo, useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, CreditCard, FileCheck, Eye } from 'lucide-react';

const STEP_ICONS = { student: User, slot: Calendar, payer: User, sepa: CreditCard, summary: FileCheck };

const PreviewField = ({ spec, type = 'text', placeholder = '' }) => {
  if (!spec?.visible) return null;
  return (
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1">
        {spec.label}{spec.required ? ' *' : ''}
      </div>
      {type === 'textarea'
        ? <Textarea rows={2} disabled placeholder={placeholder} className="bg-white" />
        : <Input disabled type={type === 'date' ? 'date' : 'text'} placeholder={placeholder} className="h-8 bg-white" />}
    </div>
  );
};

const PreviewCustomFields = ({ customFields, step }) => (
  <>
    {customFields.filter((cf) => cf.step === step && cf.label).map((cf) => (
      <div key={cf.id}>
        {cf.type === 'checkbox' ? (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox disabled /> {cf.label}{cf.required ? ' *' : ''}
          </label>
        ) : (
          <>
            <div className="text-xs font-medium text-slate-600 mb-1">{cf.label}{cf.required ? ' *' : ''}</div>
            {cf.type === 'textarea'
              ? <Textarea rows={2} disabled className="bg-white" />
              : <Input disabled className="h-8 bg-white" placeholder={cf.type === 'select' ? (cf.options || []).join(' / ') || 'Auswahl' : ''} />}
          </>
        )}
      </div>
    ))}
  </>
);

export const FormLivePreview = ({ fc }) => {
  const steps = useMemo(() => {
    const arr = [
      { key: 'subject', label: fc.texts.step_subject_title },
      { key: 'payer', label: fc.texts.step_payer_title },
    ];
    if (fc.steps.sepa) arr.push({ key: 'sepa', label: fc.texts.step_sepa_title });
    arr.push({ key: 'student', label: fc.texts.step_student_title });
    if (fc.steps.guardian) arr.push({ key: 'guardian', label: fc.texts.step_guardian_title || 'Erziehungsberechtigte:r' });
    if (fc.steps.slot) arr.push({ key: 'slot', label: fc.texts.step_slot_title });
    arr.push({ key: 'summary', label: fc.texts.step_summary_title });
    return arr;
  }, [fc]);

  const [activeKey, setActiveKey] = useState('subject');
  useEffect(() => {
    if (!steps.some((s) => s.key === activeKey)) setActiveKey('subject');
  }, [steps, activeKey]);

  const F = (key) => fc.fields[key] || { visible: true, required: false, label: key };
  const active = steps.find((s) => s.key === activeKey) || steps[0];

  return (
    <div className="border border-slate-200 rounded-xl bg-slate-50 overflow-hidden" data-testid="form-live-preview">
      <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-2">
        <Eye className="w-4 h-4 text-brand-red" />
        <span className="text-sm font-semibold text-slate-900">Live-Vorschau</span>
        <Badge variant="secondary" className="ml-auto text-[10px]">ungespeicherte Änderungen inklusive</Badge>
      </div>

      <div className="p-4">
        {/* Mini-Stepper */}
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="form-live-preview-stepper">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[s.key];
            const isActive = s.key === active.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setActiveKey(s.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                  isActive
                    ? 'bg-brand-red border-brand-red text-white'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-brand-red/40'
                }`}
                data-testid={`form-live-preview-step-${s.key}`}
              >
                {Icon && <Icon className="w-3 h-3" />} {i + 1}. {s.label}
              </button>
            );
          })}
        </div>

        {/* Schritt-Inhalt */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
          <div className="text-sm font-semibold text-slate-900">{active.label}</div>

          {active.key === 'subject' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500">
                Dropdown mit allen Fächern — Kategorien blass als Gruppen-Überschrift, Fächer klar.
                Danach je nach Fach: Unterrichtsart (Kategorie 1) oder Kursangebote (Kategorien 2+), Unterrichtsort & Anmerkungen.
              </div>
              <div>
                <div className="text-xs font-medium text-slate-600 mb-1">Fach *</div>
                <div className="border border-slate-200 rounded-md bg-white text-xs divide-y divide-slate-100">
                  <div className="px-3 py-1.5 text-slate-400">1. Beispiel-Kategorie</div>
                  {['Klavier', 'Gitarre', 'Gesang'].map((it) => (
                    <div key={it} className="px-3 py-1.5 pl-6 text-slate-900 font-medium">{it}</div>
                  ))}
                </div>
              </div>
              <PreviewField spec={{ visible: true, required: true, label: 'Unterrichtsart' }} placeholder="4er Karte / Einzelunterricht 30 Min / Gruppe" />
              <PreviewField spec={{ visible: true, required: false, label: 'Unterrichtsort' }} placeholder="Standort wählen" />
              <PreviewField spec={F('student.notes')} type="textarea" placeholder="Anmerkungen (max. 300 Zeichen)" />
            </div>
          )}

          {active.key === 'student' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <PreviewField spec={F('student.salutation')} placeholder="An / Familie / Frau / Herrn …" />
              <PreviewField spec={F('student.title')} placeholder="Dr. …" />
              <PreviewField spec={F('student.first_name')} />
              <PreviewField spec={F('student.last_name')} />
              <PreviewField spec={F('student.gender')} placeholder="männlich / weiblich / divers …" />
              <PreviewField spec={F('student.birthdate')} placeholder="Tag / Monat / Jahr" />
              <PreviewField spec={F('student.email')} />
              <PreviewField spec={F('student.phone')} />
              <div className="sm:col-span-2 space-y-3">
                <PreviewField spec={F('student.prior_knowledge')} type="textarea" placeholder="Notenlesen, Harmonielehre …" />
                <PreviewField spec={F('student.photo_consent')} placeholder="Checkbox: Hiermit erteile ich eine Foto-Erlaubnis" />
                <PreviewCustomFields customFields={fc.custom_fields} step="student" />
              </div>
            </div>
          )}

          {active.key === 'guardian' && (
            <div className="space-y-2 text-xs text-slate-600">
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border border-slate-400 inline-block" /> Zahlungspflichtige:r ist auch erziehungsberechtigt</div>
              <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border border-slate-400 inline-block" /> Abweichende Person eingeben (Anrede, Name, Adresse, Kontakt)</div>
            </div>
          )}

          {active.key === 'slot' && (
            <div className="space-y-2 text-xs text-slate-600">
              <div>An welchen Tagen und zu welchen Uhrzeiten ist Unterricht möglich?</div>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">Montag ab 15:00 Uhr</div>
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">Mittwoch 14:00–17:00 Uhr</div>
            </div>
          )}

          {active.key === 'payer' && (
            <>
              <div className="text-xs text-slate-500">{fc.texts.step_payer_hint}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PreviewField spec={F('payer.salutation')} placeholder="An / Familie / Frau / Herrn …" />
                <PreviewField spec={F('payer.title')} placeholder="Dr. …" />
                <PreviewField spec={F('payer.first_name')} />
                <PreviewField spec={F('payer.last_name')} />
                <PreviewField spec={F('payer.street')} />
                <PreviewField spec={F('payer.house_number')} />
                <PreviewField spec={F('payer.address_extra')} />
                <PreviewField spec={F('payer.postal_code')} />
                <PreviewField spec={F('payer.city')} />
                <PreviewField spec={F('payer.district')} />
                <PreviewField spec={F('payer.gender')} placeholder="männlich / weiblich / divers …" />
                <PreviewField spec={F('payer.birthdate')} placeholder="Tag / Monat / Jahr" />
                <PreviewField spec={F('payer.email')} />
                <PreviewField spec={F('payer.phone')} />
                <div className="sm:col-span-2 space-y-3">
                  <PreviewCustomFields customFields={fc.custom_fields} step="payer" />
                </div>
              </div>
            </>
          )}

          {active.key === 'sepa' && (
            <>
              <div className="grid grid-cols-1 gap-3">
                <PreviewField spec={{ visible: true, required: true, label: 'IBAN' }} placeholder="DE00 0000 0000 … (mit Live-Prüfung)" />
                <label className="flex items-start gap-2 text-xs text-slate-600">
                  <Checkbox disabled className="mt-0.5" /> Hiermit erteile ich ein SEPA-Lastschriftmandat *
                </label>
                <div className="text-xs text-slate-500">Toggle: Abweichende:r Kontoinhaber:in (Standard: Zahler)</div>
              </div>
            </>
          )}

          {active.key === 'summary' && (
            <div className="space-y-2">
              <div className="text-xs text-slate-500">Münsterland-Card: Ja/Nein, bei Ja die Kartennummer</div>
              <div className="text-xs text-slate-500">Zusammenfassung aller Angaben + Rechtstexte (AGB · Datenschutz · Widerruf)</div>
              {['Ich akzeptiere die AGB.', 'Ich stimme der Datenschutzerklärung zu.', 'Widerrufsbelehrung zur Kenntnis genommen.'].map((t) => (
                <label key={t} className="flex items-center gap-2 text-xs text-slate-600">
                  <Checkbox disabled /> {t}
                </label>
              ))}
              <div className="pt-2">
                <Button disabled size="sm" data-testid="form-live-preview-submit">
                  <FileCheck className="w-3 h-3 mr-1" /> {fc.texts.submit_button}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
